import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useSubscribeNewsletter } from "../../api/auth";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";

// ✅ phone input
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import CustomHeader from "../../components/Auth/CustomHeader";

const Newsletter = (props) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState("/client-onboarding");

  document.title = "Newsletter | JobArk";

  // ✅ call the hook once
  const mutation = useSubscribeNewsletter();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: "",
      fullName: "",
      email: "",
      phoneNumber: "", // react-phone-input-2 returns digits only by default (no "+")
      consent: false,
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Please Enter Your Company Name"),
      fullName: Yup.string().required("Please Enter Your Full Name"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Please Enter Your Email"),
      phoneNumber: Yup.string()
        .required("Please Enter Your Phone Number")
        .matches(/^[0-9]+$/, "Phone number must contain only digits")
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number can't be longer than 15 digits"),
      consent: Yup.boolean().oneOf([true], "You must consent to continue"),
    }),
    onSubmit: (values, { resetForm }) => {
      const payload = {
        ...values,
        role: "client",
      };
      mutation.mutate(payload, {
        onSuccess: () => {
          toastr.success("Subscribed successfully!");
          const queryString = new URLSearchParams(values).toString();
          setOnboardingUrl(`/client-onboarding?${queryString}`);
          setIsSuccessModalOpen(true);
          resetForm();
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
      });
    },
  });

  return (
    <React.Fragment>
      <CustomHeader />

      <Modal
        isOpen={isSuccessModalOpen}
        toggle={() => setIsSuccessModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setIsSuccessModalOpen(false)}>
          Thank you for registering your interest!
        </ModalHeader>
        <ModalBody>
          <p>
            We’ve successfully received your details. We’ll keep you updated
            with the latest JobArk news, platform updates and launch
            announcements until you’re ready to get started.
          </p>
          <p className="mb-0">
            In the meantime, if you have any questions, please don’t hesitate to
            contact us.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" tag="a" href="https://www.jobark.co.uk/">
            Return to Home
          </Button>
          {/* <Button
            color="secondary"
            onClick={() => props.router.navigate(onboardingUrl)}
          >
            Go to Onboarding
          </Button> */}
        </ModalFooter>
      </Modal>

      <div className="auth-interest-page">
        <Container fluid className="py-5 px-9" style={{ maxWidth: 1600 }}>
          <Row className="justify-content-between align-items-start">
            <Col lg={7} className="px-4 px-lg-5">
              <p className="auth-interest-eyebrow mb-2">Welcome to JobArk</p>
              <h1
                className="display-5 fw-bold mb-4 text-dark"
                style={{ lineHeight: 1.1 }}
              >
                Build Your Workforce{" "}
                <span className="text-secondary">with JobArk</span>
              </h1>

              <p className="mb-4 text-dark" style={{ maxWidth: 680 }}>
                JobArk connects your business with verified, PAYE-engaged
                workers through a single, dependable workforce system - whether
                you need cover for one shift or support for ongoing assignments.
              </p>

              <h2 className="h2 fw-bold mb-3 text-dark">Why Use JobArk?</h2>
              <ul className="list-unstyled mb-5" style={{ maxWidth: 680 }}>
                {[
                  "Verified workforce, ready for assignments",
                  "Built-in compliance (RTW, visa tracking, AWR, SSP)",
                  "Intelligent placement matches based on your criteria",
                  "Real-time attendance verification",
                  "Clear rate structure",
                ].map((item, i) => (
                  <li key={i} className="d-flex align-items-start mb-2">
                    <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h2 className="h2 fw-bold mb-3 text-dark">
                What You&apos;ll Need for Onboarding
              </h2>
              <p className="mb-3 text-dark" style={{ maxWidth: 760 }}>
                To activate your business account, you will need:
              </p>
              <ul className="list-unstyled mb-4" style={{ maxWidth: 760 }}>
                {[
                  "Company details",
                  "Contact information",
                  "Proof of Employers' & Public Liability Insurance",
                ].map((item, i) => (
                  <li
                    key={`onboarding-need-${i}`}
                    className="d-flex align-items-start mb-2"
                  >
                    <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mb-3 text-dark" style={{ maxWidth: 760 }}>
                Once approved, you gain full dashboard access including:
              </p>
              <ul className="list-unstyled mb-0" style={{ maxWidth: 760 }}>
                {[
                  "Live rate card",
                  "Assignment creation",
                  "Workforce management tools",
                ].map((item, i) => (
                  <li
                    key={`dashboard-access-${i}`}
                    className="d-flex align-items-start mb-2"
                  >
                    <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Col>

            <Col lg={4} className="px-4">
              <Card className="auth-interest-form-card shadow border-0">
                <div className="auth-interest-form-header">
                  <div className="text-white p-4 text-center">
                    <h5 className="text-white mb-1">Not ready to book yet?</h5>
                    <p className="mb-0 text-white-50">
                      Register your interest and a member of the JobArk team
                      will contact you.
                    </p>
                  </div>
                </div>

                <CardBody className="pt-0">
                  <div className="p-3 p-md-4">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {mutation.isSuccess && (
                        <Alert color="success">
                          Successfully registered for the newsletter!
                        </Alert>
                      )}
                      {mutation.isError && (
                        <Alert color="danger">
                          Something went wrong. Please try again.
                        </Alert>
                      )}

                      {/* Company Name */}
                      <div className="mb-3">
                        <Label className="form-label">Company Name</Label>
                        <Input
                          name="companyName"
                          type="text"
                          placeholder="Enter Company Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.companyName || ""}
                          invalid={
                            validation.touched.companyName &&
                            !!validation.errors.companyName
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.companyName}
                        </FormFeedback>
                      </div>

                      {/* Full Name */}
                      <div className="mb-3">
                        <Label className="form-label">Contact Name</Label>
                        <Input
                          name="fullName"
                          type="text"
                          placeholder="Enter Contact Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.fullName || ""}
                          invalid={
                            validation.touched.fullName &&
                            !!validation.errors.fullName
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.fullName}
                        </FormFeedback>
                      </div>

                      {/* Email */}
                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="Enter Email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email &&
                            !!validation.errors.email
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      </div>

                      {/* Phone Number */}
                      <div className="mb-3">
                        <Label className="form-label">Phone Number</Label>
                        <PhoneInput
                          country="gb"
                          enableSearch
                          containerClass="w-100"
                          inputProps={{
                            name: "phoneNumber",
                            required: true,
                            "aria-label": "Phone number",
                          }}
                          value={validation.values.phoneNumber}
                          onBlur={() =>
                            validation.setFieldTouched("phoneNumber", true)
                          }
                          onChange={(value) =>
                            validation.setFieldValue("phoneNumber", value)
                          }
                        />
                        {validation.touched.phoneNumber &&
                          validation.errors.phoneNumber && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.phoneNumber}
                            </div>
                          )}
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            id="consent"
                            name="consent"
                            type="checkbox"
                            checked={validation.values.consent}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.consent &&
                              !!validation.errors.consent
                            }
                          />
                          <Label className="form-check-label" htmlFor="consent">
                            I consent to JobArk processing my data in line with
                            the Privacy Policy.
                          </Label>
                        </div>
                        {validation.touched.consent &&
                          validation.errors.consent && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.consent}
                            </div>
                          )}
                      </div>

                      <div className="d-grid">
                        <button
                          className="btn btn-secondary btn-lg"
                          type="submit"
                          disabled={mutation.isPending}
                        >
                          {mutation.isPending
                            ? "Submitting..."
                            : "Register Interest"}
                        </button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              {/* <div className="text-center mt-3">
              <Link
                to="/client-onboarding"
                className="btn btn-light btn-sm px-3 border-0 text-secondary"
                style={{ boxShadow: "none" }}
              >
                Create Business Account
              </Link>
            </div> */}
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Newsletter.propTypes = {
  router: PropTypes.shape({
    navigate: PropTypes.func.isRequired,
  }).isRequired,
};

const NewsletterWithRouter = withRouter(Newsletter);

export default NewsletterWithRouter;
